import { Link } from 'react-router-dom';
import { Sparkles, DollarSign, MapPin, Video, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Services = () => {
  const { t } = useTranslation();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-white">
      <section className="py-8 sm:py-12 md:py-16 px-4">
        <div className="max-w-7xl mx-auto text-left mb-6 sm:mb-8">
          <h1 className="text-4xl sm:text-5xl md:text-5xl lg:text-6xl font-bold mb-4 text-pink-400" data-aos="fade-down">{t('services.title')}</h1>
          <p className="text-lg sm:text-xl text-gray-600" data-aos="fade-up" data-aos-delay="100">{t('services.subtitle')}</p>
        </div>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7 md:gap-8 mb-12 md:mb-16">
            <div className="glass-card rounded-2xl shadow-xl overflow-hidden" data-aos="fade-up">
              <div className="bg-gradient-to-br from-primary-300 to-pink-400 p-6 sm:p-7 md:p-8 text-gray-600">
                <h2 className="text-2xl sm:text-2xl md:text-3xl font-bold mb-2">🔮 {t('services.tarotReading')}</h2>
              </div>
              <div className="p-6 sm:p-7 md:p-8">
                <div className="space-y-4 mb-6">
                  <p className="text-gray-600 text-base sm:text-base md:text-lg">{t('services.tarotDesc')}</p>
                </div>
                <div className="border-t border-gray-200 pt-12">
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-gray-600 text-sm sm:text-base">💰 {t('services.tarotWeekday')}</span>
                      <span className="text-lg sm:text-xl font-bold text-gray-600 whitespace-nowrap">$30.00 USD</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-gray-600 text-sm sm:text-base">💰 {t('services.tarotWeekend')}</span>
                      <span className="text-lg sm:text-xl font-bold text-gray-600 whitespace-nowrap">$40.00 USD</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mb-[4rem] italic">{t('services.tarotNote')}</p>
                  <Link
                    to="/contact"
                    className="block w-full bg-gradient-to-r from-primary-300 to-pink-400 hover:from-primary-400 hover:to-pink-500 text-gray-600 text-center px-6 py-4 rounded-full font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
                  >
                    {t('services.bookNow')}
                  </Link>
                </div>
              </div>
            </div>

            <div className="glass-card rounded-2xl shadow-xl overflow-hidden" data-aos="fade-up" data-aos-delay="100">
              <div className="bg-gradient-to-br from-primary-300 to-pink-400 p-6 sm:p-7 md:p-8 text-gray-600">
                <h2 className="text-2xl sm:text-2xl md:text-3xl font-bold mb-2">🪶 {t('services.runasReading')}</h2>
              </div>
              <div className="p-6 sm:p-7 md:p-8">
                <div className="space-y-4 mb-6">
                  <p className="text-gray-600 text-base sm:text-base md:text-lg">{t('services.runasDesc')}</p>
                  <p className="text-gray-600 font-semibold text-sm sm:text-base">{t('services.runasDelivery')}</p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <span className="text-primary-300 mr-2">•</span>
                      <span>{t('services.runasFeature1')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-300 mr-2">•</span>
                      <span>{t('services.runasFeature2')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-300 mr-2">•</span>
                      <span>{t('services.runasFeature3')}</span>
                    </li>
                  </ul>
                </div>
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xl sm:text-2xl font-bold text-gray-600">$50.00 USD</span>
                    <DollarSign className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-primary-300" />
                  </div>
                  <Link
                    to="/contact"
                    className="block w-full bg-gradient-to-r from-primary-300 to-pink-400 hover:from-primary-400 hover:to-pink-500 text-gray-600 text-center px-6 py-4 rounded-full font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
                  >
                    {t('services.bookNow')}
                  </Link>
                </div>
              </div>
            </div>

            <div className="glass-card rounded-2xl shadow-xl overflow-hidden" data-aos="fade-up" data-aos-delay="200">
              <div className="bg-gradient-to-br from-primary-300 to-pink-400 p-6 sm:p-7 md:p-8 text-gray-600">
                <h2 className="text-2xl sm:text-2xl md:text-3xl font-bold mb-2">🌌 {t('services.astroConsultation')}</h2>
              </div>
              <div className="p-6 sm:p-7 md:p-8">
                <div className="space-y-4 mb-6">
                  <div>
                    <p className="text-gray-600 font-semibold mb-2 text-sm sm:text-base">{t('services.natalChart')}</p>
                    <p className="text-gray-600 text-sm sm:text-base">{t('services.natalChartDesc')}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 font-semibold mb-2 text-sm sm:text-base">{t('services.natalTransits')}</p>
                    <p className="text-gray-600 text-sm sm:text-base">{t('services.natalTransitsDesc')}</p>
                  </div>
                </div>
                <div className="border-t border-gray-200 pt-6">
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-gray-600 text-sm sm:text-base">{t('services.natalChart')}</span>
                      <span className="text-lg sm:text-xl font-bold text-gray-600 whitespace-nowrap">$80.00 USD</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-gray-600 text-sm sm:text-base">{t('services.natalTransits')}</span>
                      <span className="text-lg sm:text-xl font-bold text-gray-600 whitespace-nowrap">$100.00 USD</span>
                    </div>
                  </div>
                  <Link
                    to="/contact"
                    className="block w-full bg-gradient-to-r from-primary-300 to-pink-400 hover:from-primary-400 hover:to-pink-500 text-gray-600 text-center px-6 py-4 rounded-full font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
                  >
                    {t('services.bookNow')}
                  </Link>
                </div>
              </div>
            </div>

            <div className="glass-card rounded-2xl shadow-xl overflow-hidden" data-aos="fade-up" data-aos-delay="300">
              <div className="bg-gradient-to-br from-primary-300 to-pink-400 p-6 sm:p-7 md:p-8 text-gray-600">
                <h2 className="text-2xl sm:text-2xl md:text-3xl font-bold mb-2">🃏 {t('services.lenormandReading')}</h2>
              </div>
              <div className="p-6 sm:p-7 md:p-8">
                <div className="space-y-4 mb-6">
                  <p className="text-gray-600 text-base sm:text-base md:text-lg">{t('services.lenormandDesc')}</p>
                </div>
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xl sm:text-2xl font-bold text-gray-600">$20.00 USD</span>
                    <DollarSign className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-primary-300" />
                  </div>
                  <Link
                    to="/contact"
                    className="block w-full bg-gradient-to-r from-primary-300 to-pink-400 hover:from-primary-400 hover:to-pink-500 text-gray-600 text-center px-6 py-4 rounded-full font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
                  >
                    {t('services.bookNow')}
                  </Link>
                </div>
              </div>
            </div>

            <div className="glass-card rounded-2xl shadow-xl overflow-hidden" data-aos="fade-up" data-aos-delay="400">
              <div className="bg-gradient-to-br from-primary-300 to-pink-400 p-6 sm:p-7 md:p-8 text-gray-600">
                <h2 className="text-2xl sm:text-2xl md:text-3xl font-bold mb-2">🔮 {t('services.sibilaReading')}</h2>
              </div>
              <div className="p-6 sm:p-7 md:p-8">
                <div className="space-y-4 mb-12">
                  <p className="text-gray-600 text-base sm:text-base md:text-lg">{t('services.sibilaDesc')}</p>
                </div>
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xl sm:text-2xl font-bold text-gray-600">$15.00 USD</span>
                    <DollarSign className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-primary-300" />
                  </div>
                  <Link
                    to="/contact"
                    className="block w-full bg-gradient-to-r from-primary-300 to-pink-400 hover:from-primary-400 hover:to-pink-500 text-gray-600 text-center px-6 py-4 rounded-full font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
                  >
                    {t('services.bookNow')}
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="glass-card text-gray-600 rounded-2xl shadow-xl p-6 sm:p-8 md:p-10 lg:p-12 mb-12 md:mb-16" data-aos="zoom-in">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-center text-pink-400">{t('services.howItWorks')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-7 md:gap-8">
              <div className="text-center" data-aos="fade-up" data-aos-delay="100">
                <div className="glass-card-primary w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Video className="w-8 h-8" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-2 text-pink-400">{t('services.connectOnline')}</h3>
                <p className="text-gray-600 text-sm sm:text-base">{t('services.connectOnlineDesc')}</p>
              </div>
              <div className="text-center" data-aos="fade-up" data-aos-delay="200">
                <div className="glass-card-primary w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-2 text-pink-400">{t('services.scheduleSession')}</h3>
                <p className="text-gray-600 text-sm sm:text-base">{t('services.scheduleSessionDesc')}</p>
              </div>
              <div className="text-center" data-aos="fade-up" data-aos-delay="300">
                <div className="glass-card-primary w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-2 text-pink-400">{t('services.receiveGuidance')}</h3>
                <p className="text-gray-600 text-sm sm:text-base">{t('services.receiveGuidanceDesc')}</p>
              </div>
            </div>
          </div>

          <div className="glass-card text-gray-600 rounded-2xl shadow-xl p-6 sm:p-8 md:p-10 lg:p-12" data-aos="fade-up">
            <h2 className="text-2xl sm:text-3xl font-bold text-pink-400 mb-6">{t('services.paymentMethods')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-7 md:gap-8">
              <div className="glass-card rounded-xl p-4 sm:p-5 md:p-6" data-aos="slide-right" data-aos-delay="100">
                <h3 className="text-xl sm:text-2xl font-bold text-pink-400 mb-4">{t('services.internationalClients')}</h3>
                <p className="text-gray-600 mb-4 text-sm sm:text-base">{t('services.paymentVia')}</p>
                <div className="space-y-2 text-gray-600 text-sm sm:text-base">
                  <p className="flex justify-between">
                    <span>{t('services.tarotWeekdayPrice')}</span>
                    <span className="font-bold">$30.00 USD</span>
                  </p>
                  <p className="flex justify-between">
                    <span>{t('services.tarotWeekendPrice')}</span>
                    <span className="font-bold">$40.00 USD</span>
                  </p>
                  <p className="flex justify-between">
                    <span>{t('services.runasPrice')}</span>
                    <span className="font-bold">$50.00 USD</span>
                  </p>
                  <p className="flex justify-between">
                    <span>{t('services.natalChartPrice')}</span>
                    <span className="font-bold">$80.00 USD</span>
                  </p>
                  <p className="flex justify-between">
                    <span>{t('services.natalTransitsPrice')}</span>
                    <span className="font-bold">$100.00 USD</span>
                  </p>
                  <p className="flex justify-between">
                    <span>{t('services.lenormandPrice')}</span>
                    <span className="font-bold">$20.00 USD</span>
                  </p>
                  <p className="flex justify-between">
                    <span>{t('services.sibilaPrice')}</span>
                    <span className="font-bold">$15.00 USD</span>
                  </p>
                </div>
              </div>
              <div className="glass-card rounded-xl p-4 sm:p-5 md:p-6" data-aos="slide-left" data-aos-delay="200">
                <div className="flex items-start space-x-2 mb-4">
                  <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-primary-300 flex-shrink-0 mt-1" />
                  <h3 className="text-xl sm:text-2xl font-bold text-pink-400">{t('services.cubansInCuba')}</h3>
                </div>
                <p className="text-gray-600 mb-4 text-sm sm:text-base">{t('services.specialPricing')}</p>
                <div className="space-y-2 text-gray-600 font-semibold text-sm sm:text-base">
                  <p>+53 58026042</p>
                  <p>+53 63131261</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};

export default Services;
